import { useState, useEffect, useCallback } from 'react';
import { useAccount } from 'wagmi';
import { API_BASE } from '@/lib/sovereign';

interface BetaCheckResponse {
  allowed: boolean;
  reason: string;
  launchDate: string;
}

export function useBetaAccess() {
  const { address, isConnected } = useAccount();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [reason, setReason] = useState<string>('');
  const [launchDate, setLaunchDate] = useState<Date>(new Date('2026-03-30T13:00:00Z'));
  const [loading, setLoading] = useState(true);

  const LAUNCH_MS = new Date('2026-03-30T13:00:00Z').getTime();

  const checkAccess = useCallback(async () => {
    // If launch date is in the past, allow immediately without waiting for API.
    if (Date.now() >= LAUNCH_MS) {
      setAllowed(true);
      setReason('launched');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const query = isConnected && address ? `?address=${encodeURIComponent(address)}` : '';
      const res = await fetch(`${API_BASE}/api/v1/beta-check${query}`);
      if (res.ok) {
        const data: BetaCheckResponse = await res.json();
        setAllowed(data.allowed);
        setReason(data.reason);
        setLaunchDate(new Date(data.launchDate));
      } else {
        setAllowed(false);
        setReason('error');
      }
    } catch {
      setAllowed(false);
      setReason('error');
    } finally {
      setLoading(false);
    }
  }, [address, isConnected, LAUNCH_MS]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return { allowed, reason, launchDate, loading, recheckAccess: checkAccess };
}
